/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { get, post, put, del } from '@/services/api/client';
import { ApiResponse } from '@/services/api/types';
import {
  SubscriptionSummary,
  InitiatePaymentResponse,
  ModuleSubscriptionItem,
  CreateSubscriptionRequest,
  AddModuleRequest,
  ChangeTierRequest,
} from '@/types/superadmin.types';
import type { FeatureAddonItem } from '@/types/addon.types';

const BASE_URL = '/billing/my-subscription';

export const mySubscriptionService = {
  async getMySubscription(): Promise<SubscriptionSummary> {
    const response = await get<ApiResponse<SubscriptionSummary>>(BASE_URL);
    return response.data;
  },

  async reactivateSubscription(): Promise<SubscriptionSummary> {
    const response = await post<ApiResponse<SubscriptionSummary>>(`${BASE_URL}/reactivate`);
    return response.data;
  },

  async initiatePayment(paymentMethod?: string): Promise<InitiatePaymentResponse> {
    const params = paymentMethod ? `?paymentMethod=${paymentMethod}` : '';
    const response = await post<ApiResponse<InitiatePaymentResponse>>(`${BASE_URL}/pay${params}`, {});
    return response.data;
  },

  async createSubscription(data: CreateSubscriptionRequest): Promise<SubscriptionSummary> {
    const response = await post<ApiResponse<SubscriptionSummary>>(BASE_URL, data);
    return response.data;
  },

  async updateSubscription(data: CreateSubscriptionRequest): Promise<SubscriptionSummary> {
    const response = await put<ApiResponse<SubscriptionSummary>>(BASE_URL, data);
    return response.data;
  },

  async addModule(data: AddModuleRequest): Promise<ModuleSubscriptionItem> {
    const response = await post<ApiResponse<ModuleSubscriptionItem>>(`${BASE_URL}/modules`, data);
    return response.data;
  },

  async previewTierChangeImpact(moduleCode: string, newTierCode: string): Promise<FeatureAddonItem[]> {
    const response = await get<ApiResponse<FeatureAddonItem[]>>(
      `${BASE_URL}/modules/${moduleCode}/tier-change-impact`, { newTierCode }
    );
    return response.data;
  },

  async changeTier(moduleCode: string, data: ChangeTierRequest): Promise<ModuleSubscriptionItem> {
    const response = await put<ApiResponse<ModuleSubscriptionItem>>(
      `${BASE_URL}/modules/${moduleCode}/tier`, data
    );
    return response.data;
  },

  async updateUserCount(moduleCode: string, userCount: number): Promise<ModuleSubscriptionItem> {
    const response = await put<ApiResponse<ModuleSubscriptionItem>>(
      `${BASE_URL}/modules/${moduleCode}/users?userCount=${userCount}`, {}
    );
    return response.data;
  },

  async removeModule(moduleCode: string, reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await del<ApiResponse<void>>(`${BASE_URL}/modules/${moduleCode}${params}`);
  },

  async convertTrial(moduleCode: string): Promise<ModuleSubscriptionItem> {
    const response = await post<ApiResponse<ModuleSubscriptionItem>>(
      `${BASE_URL}/modules/${moduleCode}/convert-trial`
    );
    return response.data;
  },

  async toggleFeature(moduleCode: string, featureCode: string, enabled: boolean): Promise<Record<string, boolean>> {
    const response = await put<ApiResponse<Record<string, boolean>>>(
      `${BASE_URL}/modules/${moduleCode}/features/${featureCode}?enabled=${enabled}`, {}
    );
    return response.data;
  },
};
